import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, Injector, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Route } from '@angular/router';
import { HamSideBarComponent } from '@modeso/aumico-components/components/ham-side-bar/ham-side-bar.component';
import {
  AnnouncementsProvider,
  SidenavService,
  Survey,
  SurveysProvider,
  USER_SURVEY_STATUS,
  animateText,
  onSideNavChange,
  userNotificationsActions,
} from '@modeso/aumico-lib-core-fe';
import { selectUserNotificationsUnreadCount } from '@modeso/aumico-lib-core-fe/reducers/user-notifications.reducer';
import { GetOrganizationDto, OrganizationProvider } from '@modeso/aumico-lib-users-fe';
import { CPMMState } from '@modeso/aumico-lib-users-fe/enums/cpmm-state.enum';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { cloneDeep } from 'lodash-es';
import { Observable, filter, map, shareReplay } from 'rxjs';
import { MixpanelService } from '../../../../../mixpanel/mixpanel.service';
import { MIXPANEL_EVENTS } from '../../../../../mixpanel/mixpanelEvents.enum';
import { environment } from '../../../environments/environment';
import { Paths } from '../../enums/paths.enum';
import { RouteLabels } from '../../enums/routeLabels.enum';
import { BasePageComponent } from '../../pages/base.page';
import { AuthGuard } from '../../shared/auth.guard';
import { SurveyPollingService } from '../../shared/services/polling.service';
import { SurveyPopupService } from '../../shared/services/survey-popup.service';

@UntilDestroy()
@Component({
  selector: 'app-home',
  template: `
        <ham-side-bar [opened]="isSidebarOpen" (sideNavStateChanged)="onSideNavStateChanged($event)">
            <div sidenav>
                <a
                    *ngFor="let page of primaryNavigation"
                    (click)="onSideNavItemClick(page.data.label)"
                    [routerLink]="page.path"
                    [attr.data-testid]="page.data?.testId || null"
                    routerLinkActive="active"
                    [class.sidebar-closed]="!isSidebarOpen"
                    [class.sidebar-closed-new]="!isSidebarOpen && page.data.new"
                    class="icon-and-title primary-navigation"
                    [class.newBlueBackground]="page.data.newBlueBackground"
                >
                    <span
                        hamIconShapes
                        icon="{{ page?.data?.icon }}"
                        [variant]="'transparent'"
                        [size]="'regular'"
                        [mustBubbleEvent]="true"
                    ></span>
                    <span class="title" *ngIf="!page?.data?.notTranslated && isSidebarOpen"
                        >{{ page?.data?.label | i18next }}
                    </span>
                    <span class="title" *ngIf="page?.data?.notTranslated && isSidebarOpen"
                        >{{ page?.data?.label }}
                    </span>
                    <div *ngIf="page.data.new" class="new-tag">
                        <span>{{ 'aumico_annual_report_main_navigation_new' | i18next }}</span>
                    </div>
                </a>
            </div>

            <div content>
                <router-outlet></router-outlet>
            </div>

            <div footer>
                <a class="icon-and-title add-padding footer-titles" (click)="clickUserNotifications()">
                    <span class="notification-bell-wrapper">
                        <span
                            hamIconShapes
                            class="settings"
                            icon="assets/hamIcons/bell.svg"
                            [variant]="'transparent'"
                            [size]="'xs'"
                            [mustBubbleEvent]="true"
                        >
                        </span>
                        <span class="notification-badge" *ngIf="(unreadCount$ | async) as unread" [class.hidden]="!unread || unread === 0">
                            {{ unread }}
                        </span>
                    </span>
                    <span class="settings-title" *ngIf="isSidebarOpen">{{
                        'aumico_user_notifications_title' | i18next
                    }}</span>
                </a>
                <a
                    *ngFor="let page of organizationNavigation"
                    (click)="onSideNavItemClick(page.data.label)"
                    [routerLink]="page.path"
                    routerLinkActive="active"
                    class="icon-and-title add-padding footer-titles"
                >
                    <span
                        hamIconShapes
                        class="settings"
                        icon="{{ page?.data?.icon }}"
                        [variant]="'transparent'"
                        [size]="'xs'"
                        [mustBubbleEvent]="true"
                    >
                    </span>
                    <span class="settings-title" *ngIf="!page?.data?.notTranslated && isSidebarOpen"
                        >{{ page?.data?.label | i18next }}
                    </span>
                    <span class="settings-title" *ngIf="page?.data?.notTranslated && isSidebarOpen"
                        >{{ page?.data?.label }}
                    </span>
                </a>
                <a
                    class="icon-and-title add-padding footer-titles"
                    href="{{ environment.supportUrl }}"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <span
                        hamIconShapes
                        class="settings"
                        icon="assets/hamIcons/help.svg"
                        [variant]="'transparent'"
                        [size]="'xs'"
                        [mustBubbleEvent]="true"
                    >
                    </span>
                    <span class="settings-title" *ngIf="isSidebarOpen">{{
                        'aumico_reports_side_bar_help' | i18next
                    }}</span>
                </a>
                <a
                    class="icon-and-title add-padding footer-titles"
                    href="{{ environment.feedbackPortalUrl }}"
                    target="_blank"
                    rel="noopener noreferrer"
                    (click)="openFeedbackPortal($event)"
                >
                    <span
                        hamIconShapes
                        class="settings heart"
                        icon="assets/hamIcons/heart.svg"
                        [variant]="'transparent'"
                        [size]="'xs'"
                        [mustBubbleEvent]="true"
                    >
                    </span>
                    <span class="settings-title" *ngIf="isSidebarOpen">{{
                        'aumico_reports_side_bar_feedback' | i18next
                    }}</span>
                </a>
                <div class="organization" [class.closed]="!isSidebarOpen">
                    <span class="organization-section-title" *ngIf="isSidebarOpen">
                        {{ organizationName$ | async }}
                    </span>
                    <span class="organization-section-title" *ngIf="!isSidebarOpen">
                        <ham-avatar
                            type="rounded"
                            size="regular"
                            class="organization-name-collapsed"
                            [fullName]="organizationName$ | async"
                        ></ham-avatar>
                    </span>

                    <a class="user-profile" (click)="toggleFlyoutMenu($event)">
                        <ham-avatar type="rounded" size="regular" [fullName]="userName"></ham-avatar>
                        <div class="user-details" *ngIf="isSidebarOpen">
                            <div class="user-name">{{ userName }}</div>
                            <div class="user-email">{{ userEmail }}</div>
                        </div>
                        <span
                            hamIconShapes
                            *ngIf="isSidebarOpen"
                            class="user-profile-icon"
                            icon="assets/hamIcons/chevron-sort.svg"
                            [variant]="'transparent'"
                            [size]="'xs'"
                            (click)="toggleFlyoutMenu($event)"
                            [mustBubbleEvent]="true"
                        >
                        </span>
                    </a>
                    <div
                        class="menu-flyout"
                        [class.closed]="!isSidebarOpen"
                        #menuFlyout
                        hamFlyout
                        [position]="menuPosition"
                        (closeFlyout)="closeFlyoutMenu()"
                        *ngIf="this.isMenuOpenIndex != null"
                        [ngStyle]="{ top: menuPosition.top + 'px', right: menuPosition.right + 'px' }"
                    >
                        <ul>
                            <li
                                [ngClass]="{
                                    'closed-flyout-buttons': !isSidebarOpen
                                }"
                                class="logout"
                                (click)="logout()"
                            >
                                <div class="logout-icon">
                                    <span
                                        hamIconShapes
                                        icon="assets/hamIcons/arrow-left-to-bracket.svg"
                                        [variant]="'transparent'"
                                        [size]="'regular'"
                                        [mustBubbleEvent]="true"
                                    >
                                    </span>
                                </div>
                                <span *ngIf="isSidebarOpen">
                                    {{ 'aumico_reports_main_navigation_logout' | i18next }}
                                </span>
                            </li>
                            <li (click)="openUserProfile()" [ngClass]="{ 'closed-flyout-buttons': !isSidebarOpen }">
                                <ham-avatar type="rounded" size="regular" [fullName]="userName"></ham-avatar>
                                <div class="user-name" *ngIf="isSidebarOpen">{{ userName }}</div>
                            </li>
                        </ul>
                    </div>

                    <div class="referral">
                        <aumico-referral-invite
                            [title]="referralTitle | i18next"
                            [text]="referralText | i18next"
                            [inviteReferralButtonText]="referralInviteButtonText | i18next"
                            [show]="isSidebarOpen"
                            [icon]="addIcon"
                        >
                        </aumico-referral-invite>
                    </div>
                </div>
            </div>
        </ham-side-bar>
        <app-user-notification-page
            [open]="isNotificationsOpen"
            (close)="closeUserNotifications()"
        ></app-user-notification-page>
        <ham-modal *ngIf="showFeedbackPopup" class="feedback" [showCancelButton]="'false'" [hideFooter]="true">
            <app-survey-popup (closeButtonClicked)="completeFeedbackPopup()"></app-survey-popup>
        </ham-modal>
    `,
  styleUrls: ['./home-module.page.scss'],
  animations: [onSideNavChange, animateText],
})
export class HomePage extends BasePageComponent {
  @ViewChild(HamSideBarComponent) sidenav!: HamSideBarComponent;
  @ViewChild('menuFlyout') menuFlyout!: ElementRef;

  isSidebarOpen = true;
  blockingRequestIsActive = false;
  menuPosition = { top: 0, right: 0 };
  isMenuOpenIndex: number | null = null;
  showFeedbackPopup = false;
  activeSurvey: Survey | undefined;
  isNotificationsOpen = false;
  unreadCount$!: Observable<string | number>;

  public sideNavState = true;
  public referralTitle = 'aumico_reports_referral_invite_title';
  public referralText = 'aumico_reports_referral_invite_text';
  public referralInviteButtonText = 'aumico_reports_referral_invite_invite_button';
  public addIcon = 'aumico-pattern-icon-add-nocircle';
  userEmail = this.localStorageService.getCurrentUserEmail();
  userName: string | undefined;
  private language: string | undefined;
  environment = environment;
  primaryNavigation: Route[] = [
    {
      path: `${Paths.PROJECT}`,
      canActivate: [AuthGuard],
      data: {
        label: RouteLabels.PROJECT_MAIN_NAVIGATION,
        canSeeNavigationLinkRoles: ['AnnualReportUser'],
        icon: 'assets/hamIcons/financial-statement.svg',
      },
    },
    {
      path: `${Paths.COMPANIES}`,
      canActivate: [AuthGuard],
      data: {
        label: RouteLabels.COMPANIES_MAIN_NAVIGATION,
        canSeeNavigationLinkRoles: ['AnnualReportUser'],
        icon: 'assets/hamIcons/building.svg',
      },
    },
    // REV-22155: "Personen" below "Firmen", same role, no feature flag
    {
      path: `${Paths.PERSONS}`,
      canActivate: [AuthGuard],
      data: {
        label: RouteLabels.PERSONS_MAIN_NAVIGATION,
        canSeeNavigationLinkRoles: ['AnnualReportUser'],
        icon: 'assets/hamIcons/user.svg',
        // e2e selector contract (e2e/fixtures/persons.ts)
        testId: 'nav-persons',
      },
    },
    // {
    //     path: `${Paths.COMPANY_VALUATION}`,
    //     canActivate: [AuthGuard],
    //     data: {
    //         label: RouteLabels.COMPANY_VALUATION,
    //         canSeeNavigationLinkRoles: ['AnnualReportUser'],
    //         icon: 'aumico-pattern-icon-data-file-check',
    //         new: false,
    //     },
    // },
    // {
    //     path: `${Paths.COMPANY_VALUATION}`,
    //     canActivate: [AuthGuard],
    //     data: {
    //         label: RouteLabels.COMPANY_VALUATION,
    //         canSeeNavigationLinkRoles: ['AnnualReportUser'],
    //         icon: 'assets/hamIcons/company-valuation.svg',
    //         new: false,
    //     },
    // },
  ];

  organizationNavigation: Route[] = [
    {
      path: `${Paths.ORGANIZATION}/${Paths.ORGANIZATION_SETTINGS}`,
      data: {
        label: RouteLabels.ORGANIZATION_SETTINGS,
        icon: 'assets/hamIcons/organization-settings.svg',
      },
    },
  ];

  organizationName$: Observable<string>;
  userSurveyStatus: USER_SURVEY_STATUS | undefined;
  userSurveyStatusEnum = USER_SURVEY_STATUS;

  constructor(
    injector: Injector,
    private readonly sidenavService: SidenavService,
    private organizationProvider: OrganizationProvider,
    private readonly mixpanelService: MixpanelService,
    public override readonly dialog: MatDialog,
    private surveysProvider: SurveysProvider,
    private surveyPollingService: SurveyPollingService,
    private surveyPopupService: SurveyPopupService,
    private announcementsProvider: AnnouncementsProvider,
    private readonly store: Store,
    private readonly http: HttpClient,
  ) {
    super(injector);

    this.authProvider
      .getCurrentUserRoles$()
      .pipe(filter((roles) => roles != null), untilDestroyed(this))
      .subscribe((roles: string[]) => {
        this.handleCurrentUserRolesPermissions(roles);
      });

    this.surveysProvider
      .getActiveSurvey$()
      .pipe(untilDestroyed(this))
      .subscribe((activeSurvey) => {
        this.activeSurvey = cloneDeep(activeSurvey);
        if (activeSurvey != null) {
          this.surveysProvider.dispatchGetUserSurveyStatus$(activeSurvey.id);
        }
      });

    this.surveysProvider
      .getUserSurveyStatus$()
      .pipe(
        filter((status) => status != null),
        untilDestroyed(this),
      )
      .subscribe((status) => {
        this.userSurveyStatus = status.status;
      });

    this.surveyPopupService.showPopup$.pipe(untilDestroyed(this)).subscribe((isOpen) => {
      this.showFeedbackPopup = isOpen;
    });

    this.surveysProvider.dispatchGetActiveSurvey();

    this.unreadCount$ = this.store.select(selectUserNotificationsUnreadCount).pipe(
      map((count) => (count > 99 ? '99+' : count)),
    );

    console.log('design mode', environment.designMode);
    if (environment.designMode) {
      this.primaryNavigation.unshift({
        path: `../../${Paths.DESIGN}`,
        canActivate: [AuthGuard],
        data: {
          label: RouteLabels.DESIGN,
          canSeeNavigationLinkRoles: ['AnnualReportUser'],
          icon: 'assets/hamIcons/closing-process.svg',
          newBlueBackground: true,
        },
      });
    }
  }

  clickUserNotifications(): void {
    this.isNotificationsOpen = true;
    this.isSidebarOpen = false;
  }

  closeUserNotifications(): void {
    this.isNotificationsOpen = false;
  }

  override ngOnInit(): void {
    this.sidenavService.sideNavState$.pipe(untilDestroyed(this)).subscribe((res) => {
      this.sideNavState = res;
    });

    super.ngOnInit();
    this.authProvider
      .getCurrentUser$()
      .pipe(
        filter((user) => user != null),
        untilDestroyed(this),
      )
      .subscribe((user) => {
        this.localStorageService.setCurrentUserEmail(user.email);
        this.userEmail = user.email;
        this.userName = user.firstName + ' ' + user.lastName;
        this.language = user.locale;

        this.localStorageService.setCurrentOrganizationName(user.organizationName);
        if (user.preferences != null) {
          this.localStorageService.setUserPrefrences(user.preferences);
        }

        // polling service
        this.surveyPollingService.startPolling(user);
      });
    this.authProvider.dispatchGetCurrentUser();
    this.organizationName$ = this.organizationProvider.getCurrentOrganizationName$().pipe(shareReplay());

    this.organizationProvider
      .getCurrentOrganization$()
      .pipe(
        filter((organization) => organization != null),
        untilDestroyed(this),
      )
      .subscribe((organization: GetOrganizationDto) => {
        this.removeClosingProcessTaskFromPrimaryNavigation();
        if (
          organization.cpmmActivationDetails?.activationState === CPMMState.ACTIVE &&
          organization.isMandateAndTaskManagementEnabled
        ) {
          this.primaryNavigation.push({
            path: `${Paths.TASKS}`,
            canActivate: [AuthGuard],
            data: {
              label: RouteLabels.TASKS,
              canSeeNavigationLinkRoles: ['AnnualReportUser'],
              icon: 'assets/hamIcons/closing-process.svg',
            },
          });
        }
      });

    this.organizationProvider.dispatchGetCurrentOrganization();

    // Always load notifications on app load
    this.store.dispatch(
      userNotificationsActions.loadUserNotifications({
        request: { offset: 0, limit: 100, filter: { archived: false, read: undefined } },
      }),
    );
  }

  private removeClosingProcessTaskFromPrimaryNavigation(): void {
    const closingProcessTaskIndex = this.primaryNavigation.findIndex(
      (item) => item.data['label'] === RouteLabels.TASKS || item.data['label'] === RouteLabels.CLOSING_AND_TASKS,
    );
    if (closingProcessTaskIndex > -1) {
      this.primaryNavigation.splice(closingProcessTaskIndex, 1);
    }
  }

  onSideNavItemClick(pageLabel: RouteLabels): void {
    this.isNotificationsOpen = false;
    this.announcementsProvider.dispatchGetUserAnnouncements();

    if (pageLabel === RouteLabels.COMPANY_VALUATION) {
      this.mixpanelService.track(MIXPANEL_EVENTS.COMPANY_VALUATION_SIDE_NAV_CLICK);
    }
  }

  toggleFlyoutMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.isMenuOpenIndex = 0;

    const buttonElement = event.target as HTMLElement;
    const rect = buttonElement.getBoundingClientRect();

    let top;
    let right;
    if (this.isSidebarOpen) {
      top = rect.top - 120;
      right = window.innerWidth - rect.right - 170;
    } else {
      top = rect.top - 120;
      right = window.innerWidth - rect.right - 20;
    }

    this.menuPosition = { top, right };
  }

  closeFlyoutMenu(): void {
    this.isMenuOpenIndex = null;
  }

  logout(): void {
    this.router.navigate([`${this.language}/${Paths.LOGOUT}`]);
    this.closeFlyoutMenu();
  }

  openUserProfile(): void {
    this.isNotificationsOpen = false;
    this.router.navigate([`${this.language}/home/${Paths.USER_ACCOUNT}`]);
    this.closeFlyoutMenu();
  }

  onSideNavStateChanged(isOpened: boolean): void {
    if (isOpened) {
      this.isNotificationsOpen = false;
    }
    this.isSidebarOpen = isOpened;
  }

  completeFeedbackPopup(): void {
    this.surveysProvider.resetCurrentQuestion();
    this.showFeedbackPopup = false;
    this.surveysProvider.dispatchGetUserSurveyStatus$(this.activeSurvey.id);
    this.surveyPopupService.closePopup();
  }

  openFeedbackPortal(event?: Event): void {
    event?.preventDefault();

    const feedbackWindow = window.open('', '_blank');
    if (feedbackWindow) {
      feedbackWindow.opener = null;
    }

    this.http
      .post<{ token: string }>(`${environment.modeso.apiGateway}auth/feedback-token`, {})
      .subscribe({
        next: ({ token }) => {
          const feedbackUrl = `${environment.feedbackPortalUrl}?token=${encodeURIComponent(token)}`;
          if (feedbackWindow) {
            feedbackWindow.location.href = feedbackUrl;
          } else {
            window.open(feedbackUrl, '_blank', 'noopener,noreferrer');
          }
        },
        error: () => {
          if (feedbackWindow) {
            feedbackWindow.location.href = environment.feedbackPortalUrl;
          } else {
            window.open(environment.feedbackPortalUrl, '_blank', 'noopener,noreferrer');
          }
        },
      });
  }

  private handleCurrentUserRolesPermissions(roles: string[]): void {
    if (!roles.includes('ORG_ADMIN')) {
      this.organizationNavigation = [];
    }
  }
}
